import React, { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import dummyLocations from "../data/locationsData";
import PropTypes from "prop-types";
import TWA from "../assets/TWA.jpg";
import SLU from "../assets/logo_image.png";
import Clock from "../assets/slu-clock-tower.jpg";

import SaintLouisImage from "../assets/slu-logo-blue.png";
import ROC from "../assets/contact-images/ROC-Logo-T3.png";
import JTNL from "../assets/contact-images/JTNL_Logo_2021.png";
import jobfair from "../assets/job_fair.png";
import VictoryMission from "../assets/contact-images/victory-mission.png";

// Import fetchContactStaffByRegion function

import { fetchContactStaffByRegion } from "../api/controller/admin/adminContactStaff";
import { submitContactUs } from "../api/controller/contactUs";

//  Contact Form Component
const ContactForm = ({
  contact,
  setContact,
  contactStatus,
  handleContactSubmit,
}) => (
  <div className="bg-white rounded-xl p-6 shadow-lg">
    <h3 className="text-xl font-semibold mb-2 font-['corporate-s'] text-[#003DA5]">
      Contact SLU TWA
    </h3>
    <p className="text-sm text-gray-600 mb-4">
      For sponsorships, volunteering, or hiring questions — send us a message.
    </p>
    <label htmlFor="name" className="block text-sm text-gray-700 mb-1">
      Name
    </label>
    <input
      id="name"
      value={contact.name}
      onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
      className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-600"
      required
      aria-required="true"
    />
    <label htmlFor="email" className="block text-sm text-gray-700 mb-1">
      Email
    </label>
    <input
      id="email"
      type="email"
      value={contact.email}
      onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
      className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-600"
      required
      aria-required="true"
    />
    <label htmlFor="message" className="block text-sm text-gray-700 mb-1">
      Message
    </label>
    <textarea
      id="message"
      value={contact.message}
      onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))}
      rows="5"
      className="w-full mb-4 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-600"
      required
      aria-required="true"
    />
    <div className="flex items-center gap-3 justify-center">
      <button
        onClick={handleContactSubmit}
        className="px-4 py-2 bg-[#003DA5] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
        aria-label="Send Message"
      >
        Send Message
      </button>
      <a
        className="px-4 py-2 border rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-600"
        href={`mailto:twa@slu.edu?subject=${encodeURIComponent(
          "SLU TWA Contact"
        )}&body=${encodeURIComponent(
          `Name: ${contact.name || ""}\nEmail: ${contact.email || ""}\n\n${
            contact.message || ""
          }`
        )}`}
        aria-label="Open email client"
      >
        Or open email
      </a>
    </div>
    {contactStatus && (
      <div
        className={`mt-4 text-sm text-center ${
          contactStatus.type === "error" ? "text-rose-600" : "text-green-700"
        }`}
      >
        {contactStatus.text}
      </div>
    )}
  </div>
);

ContactForm.propTypes = {
  contact: PropTypes.object.isRequired,
  setContact: PropTypes.func.isRequired,
  contactStatus: PropTypes.object,
  handleContactSubmit: PropTypes.func.isRequired,
};

const ContactPage = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState(null);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [locations, setLocations] = useState([]);

  async function handleContactSubmit(e) {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      setContactStatus({ type: "error", text: "Please complete all fields." });
      return;
    }

    setContactStatus({ type: "sending", text: "Sending..." });

    try {
      const payload = {
        name: contact.name?.trim() || "",
        email: contact.email?.trim() || "",
        message: contact.message?.trim() || "",
      };

      await submitContactUs(payload);
      setContactStatus({
        type: "success",
        text: "Message sent! We’ll be in touch.",
      });
      setContact({ name: "", email: "", message: "" });
      setTimeout(() => setContactOpen(false), 1200);
    } catch (error) {
      console.error("Contact submission failed:", error);
      setContactStatus({
        type: "error",
        text: "Failed to send message. Please try again.",
      });
    }
  }

  //    useEffect(() => {
  //     // Check if the URL has a hash (e.g., #st.-louis)
  //     if (window.location.hash) {
  //         // Use setTimeout to ensure the DOM elements are rendered before we try to scroll
  //         // This is a common pattern in React to wait for the DOM to be ready
  //         setTimeout(() => {
  //             // Get the ID string without the '#'
  //             const id = window.location.hash.substring(1);
  //             // Find the element with that specific ID
  //             const element = document.getElementById(id);

  //             if (element) {
  //                 // Scroll smoothly to the element
  //                 element.scrollIntoView({ behavior: 'smooth' });
  //             }
  //         }, 0);
  //     } else {
  //       // Optional: If navigating to /contact without a specific hash, start at the top
  //       window.scrollTo(0, 0);
  //     }
  // }, []); // Empty dependency array ensures this runs once on component mount

  useEffect(() => {
    if (!locations.length) return;

    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.substring(1);

    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }, [locations]);

  useEffect(() => {
    const fetchData = async () => {
      const stlstaff = await fetchContactStaffByRegion(
        "68d6ce9712a74e4e2976f6da"
      );
      const columbiaStaff = await fetchContactStaffByRegion(
        "68d847ebc0ff61b8057dff28"
      );
      const springfieldStaff = await fetchContactStaffByRegion(
        "68d8482bc0ff61b8057dff2e"
      );
      const kansasCityStaff = await fetchContactStaffByRegion(
        "68d6d32e41ff4e7490efee3e"
      );
      const locations = [
        {
          city: "St. Louis",
          cityImage1: SaintLouisImage,
          cityImage2: SLU,
          description:
            "Headquartered in Saint Louis, on the campus of Saint Louis University Brouster Hall. SLU TWA serves as a practical extension of Saint Louis University’s Jesuit mission, embodying a faith that does justice by directly addressing systemic inequalities and empowering individuals through education and employment.",
          staff: stlstaff || [],
        },
        {
          city: "Kansas City",
          cityImage1: JTNL,
          cityImage2: SLU,
          description:
            "In the Kansas City area, TWA is collocated with Journey to New Life (JTNL). Founded in 2013 in response to the lack of services for persons reentering society following prison, JTNL provides first-stop services, residence, case management, employment assistance and peer support in a safe and supportive environment. At JTNL, TWA works primarily with individuals who began services pre-release (at FCC, MECC, NECC, or WERDCC) to connect them with employment in Kansas City. TWA collaborates with JTNL to refer to wraparound services offered at the agency and beyond. The program also cultivates fair chance employer partnerships to be shared by participants from both organizations.",
          staff: kansasCityStaff || [],
        },
        {
          city: "Springfield",
          cityImage1: VictoryMission,
          cityImage2: SLU,
          description:
            "In the Springfield region of Missouri, TWA collocates at Victory Mission. Through various programs including a mobile food pantry, emergency shelter, residential recovery, and workforce development, Victory Mission seeks to help people leaving poverty to rebuild their lives and gain independence. Many of their participants are justice-involved and seeking to overcome barriers to employment. At Victory Mission, TWA works primarily with individuals who began services pre-release (at FCC, MECC, NECC, or WERDCC) to connect them with employment in Columbia and the surrounding areas. TWA coordinates with Victory Mission to refer to wraparound services offered at the agency and beyond. The program also cultivates fair chance employer partnerships to be shared by participants from both organizations.",
          staff: springfieldStaff || [],
        },
        {
          city: "Columbia",
          cityImage1: ROC,
          cityImage2: SLU,
          description:
            "Saint Louis University Transformative Workforce Academy is in partnership with in2Action. In the Columbia region in Missouri, TWA collaborates with in2Action. In2Action provides transition and recovery support services to people recently released from prison and those at risk of incarceration. By focusing on relationships, in2Action provides safe housing, basic needs, case management and counseling to introduce people to an entire community of healthy people. Their Reentry Opportunity Center (ROC Center), where TWA collocates, is a place where people who are justice-involved come to access support and resources to reenter society and thrive. At in2Action, TWA works primarily with individuals who began services pre-release (at FCC, MECC, NECC, or WERDCC) to connect them with employment in Columbia and the surrounding areas. TWA coordinates with in2Action to refer to wraparound services offered at the agency and beyond. The program also cultivates fair chance employer partnerships to be shared by participants from both organizations.",
          staff: columbiaStaff || [],
        },
      ];
      setLocations(locations || dummyLocations);
    };
    fetchData();
  }, []);

  function truncateBio(text) {
    if (text.length <= 100) return text;
    return text.slice(0, 100).trim() + "...";
  }

  const openStaffModal = (member) => {
    setSelectedStaff(member);
    setStaffModalOpen(true);
  };

  return (
    <div className="font-['corporate-s'] text-[#2b2b2b] antialiased">
      {/* Header */}
      <header className="relative">
        <img
          src={Clock}
          alt="Contact background"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] object-cover"
        />
        <div className="absolute inset-0 bg-blue-900 opacity-60"> </div>
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <h1 className="text-[2rem] sm:text-[3rem] md:text-[4.25rem] font-bold font-['corporate-s'] text-[#FFF] my-4 text-center">
            Contact
          </h1>
        </div>
      </header>
      {/* OVERLAPPING CONTENT BOX */}
      {/* <section className="relative bg-gray-200 py-16 h-[50px] md:h-[150px]">
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center px-6">
          <div className="bg-white relative p-6 md:px-6 md:w-[300px] shadow-xl mx-auto max-w-sm h-lg md:max-w-xl">
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
              Connect With Us in Your Region
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-2xl font-bold font-['corporate-s']  mb-4 text-[#003DA5] text-center">
              St. Louis
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate-s']  mb-4 text-[#003DA5] text-center">
              Kansas City
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate-s']  mb-4 text-[#003DA5] text-center">
              SpringField
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate-s']  mb-4 text-[#003DA5] text-center">
              Columbia
            </h2>
          </div>
        </div>
      </section> */}

  <section className="relative bg-gray-200 py-16 mt-10 h-[50px] md:h-[150px]">
        {/* OVERLAPPING CONTENT BOX */}
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center px-6">
          <div className="bg-white relative p-6 md:px-6 md:w-[300px] shadow-xl mx-auto max-w-sm h-lg md:max-w-xl">
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
                Connect With Us in Your Region
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
                St. Louis
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
              Kansas City
            </h2>
             <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
              SpringField
            </h2>
            <hr className="w-full h-1 my-4 mx-auto bg-gray-300 border-0" />
            <h2 className="text-xl sm:text-lg md:text-xl font-bold font-['corporate']  mb-4 text-[#003DA5] text-center">
             Columbia
            </h2>
          </div>
        </div>
      </section>




      <section>
        <div className="grid py-8 pt-50 px-0 place-items-center bg-[#f0f0f0]">
          <div className="flex flex-col md:flex-row items-center gap-6 bg-[#d6d6d6] md:h-[500px]">
            <div className="md:w-1/3 h-full relative">
              {/* Image container (on the right on large screens) */}
              <img
                src={jobfair}
                alt="Volunteers"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Yellow Line + Text */}
            <div className="md:w-2/3 relative relative">
              <div className="pl-6 pt-10 pb-10">
                <p className="text-base md:text-xl lg:text-md xl:text-xl text-left my-4 px-2 font-['corporate-s'] border-l-6 border-[#FFD700] leading-relaxed">
                  SLU TWA operates across four regions, and our team is ready to
                  connect with you—whether you're a jobseeker or interested in
                  volunteering, donating, sponsoring, partnering, or joining our
                  team. No matter your location, we’re here to support your
                  involvement and answer any questions. To get in touch with the
                  regional contact nearest you, please email us at{" "}
                  <a
                    href="mailto:twa@slu.edu"
                    className="underline text-[#003DA5] hover:text-white"
                  >
                    twa@slu.edu
                  </a>{" "}
                  {/* or contact Meredith Rataj at{" "}
            <a href="mailto:meredith.rj@slu.edu" className="underline text-sky-200 hover:text-white">
              meredith.rj@slu.edu
            </a>. */}
                  .
                </p>
                <p className="mt-4 max-w-3xl text-lg md:text-xl font-bold text-[#00244D] text-center mx-auto font-['corporate-s']">
                  Let’s work together to create pathways to opportunity and
                  lasting impact.
                </p>
                <div className="mt-8 flex flex-wrap gap-4 mr-10 justify-end">
                  <button
                    onClick={() => setContactOpen(true)}
                    className="inline-flex items-center gap-3 px-5 text-[#f0f0f0] py-3 bg-[#00244D] rounded-lg font-semibold font-['corporate-s'] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    aria-label="Open contact form"
                  >
                    <Mail className="w-5 h-5 text-[#f0f0f0]" /> Get in Touch
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 hidden md:block opacity-20">
          <svg viewBox="0 0 600 800" className="w-full h-full">
            <defs>
              <linearGradient id="g1" x1="0" x2="1">
                <stop offset="0" stopColor="#ffffff" stopOpacity="0.02" />
                <stop offset="1" stopColor="#000000" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <rect width="600" height="800" fill="url(#g1)" />
          </svg>
        </div>
      </section>
      {/* <img   
          src={Clock}
          alt="Contact background"
        className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] object-cover" />
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 relative z-10">
          <h1 className="text-4xl md:text-6xl font-['Crimson_Pro'] leading-tight drop-shadow text-center text-[#00244D]">
            Contact
          </h1>
          <h2 className="text-2xl text-[#003DA5] md:text-3xl font-semibold font-['HVD_Trial_Brandon_Grotesque'] mt-4 mb-4 text-center uppercase">
            Connect With Us in Your Region
          </h2>
          <p className="mt-6 max-w-3xl text-md font-['corporate-s'] text- md:text-lg text-left  mx-auto">
            SLU TWA operates across four regions, and our team is ready to connect with you—whether you're a jobseeker or interested in volunteering, donating, sponsoring, partnering, or joining our team. No matter your location, we’re here to support your involvement and answer any questions. To get in touch with the regional contact nearest you, please email us at{" "}
            <a href="mailto:twa@slu.edu" className="underline text-sky-200 hover:text-white">
              twa@slu.edu
            </a>{" "}
            {/* or contact Meredith Rataj at{" "}
            <a href="mailto:meredith.rj@slu.edu" className="underline text-sky-200 hover:text-white">
              meredith.rj@slu.edu
            </a>. *
          .</p>
          <p className="mt-4 max-w-3xl text-lg md:text-xl font-bold text-[#00244D] text-center mx-auto font-['HVD_Trial_Brandon_Grotesque']">
            Let’s work together to create pathways to opportunity and lasting impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-3 px-5 text-[#f0f0f0] py-3 bg-[#00244D] rounded-lg font-semibold font-['HVD_Trial_Brandon_Grotesque'] focus:outline-none focus:ring-2 focus:ring-emerald-600"
              aria-label="Open contact form"
            >
              <Mail className="w-5 h-5 text-[#f0f0f0]" /> Get in Touch
            </button>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 hidden md:block opacity-20">
          <svg viewBox="0 0 600 800" className="w-full h-full">
            <defs>
              <linearGradient id="g1" x1="0" x2="1">
                <stop offset="0" stopColor="#ffffff" stopOpacity="0.02" />
                <stop offset="1" stopColor="#000000" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <rect width="600" height="800" fill="url(#g1)" />
          </svg>
        </div>
      </header> */}

      {/* Locations Details */}
      {locations.map((location, idx) => (
        // <section
        //   key={idx}
        //   id={location.city.toLowerCase().replace(" ", "-")}
        //   className="py-16 bg-white"
        // >
        <section
          id={location.city.toLowerCase().replace(" ", "-")}
          className="py-16 bg-white scroll-mt-32"
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img
                  src={location.cityImage1}
                  alt={`${location.city} Logo 1`}
                  className="w-40 h-30 p-2  rounded-md object-contain shadow-md"
                  loading="lazy"
                  onError={(e) => (e.target.src = SLU)}
                />
                <span className="text-4xl font-bold text-[#003DA5]">+</span>
                <img
                  src={location.cityImage2}
                  alt={`${location.city} Logo 2`}
                  className="w-40 h-30 rounded-md object-contain shadow-md"
                  loading="lazy"
                  onError={(e) => (e.target.src = SLU)}
                />
              </div>

              {/* <h2 className="text-3xl font-bold mt-3 mb-4 uppercase text-[#003DA5] font-['HVD_Trial_Brandon_Grotesque']">{location.city}</h2> */}
            </div>
            <div className="grid md:grid-cols-5 py-8 grid-cols-1">
              <div className="col-start-1 col-span-1 py-10 flex justify-center items-center">
                <h2 className="text-4xl sm:text-5xl font-bold text-[#003DA5] font-['corporate-s'] text-center md:text-left tracking-tight">
                  {location.city}
                </h2>
              </div>
              <div className="md:col-start-2 col-span-4 flex justify-center items-center">
                <p className="font-['corporate-s'] text-base md:mx-10 md:text-xl lg:text-lg xl:text-xl leading-relaxed text-[#2b2b2b]">
                  {location.description}
                </p>
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-4 uppercase font-['corporate-s']">
              Contacts
            </h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {location.staff.map((member, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow flex flex-col items-center gap-4 w-full p-4 hover:shadow-lg transition-shadow duration-300"
                >
                  <img
                    src={member.image}
                    alt={`${member.name}`}
                    className="h-90 rounded-sm object-cover lazy-load"
                    loading="lazy"
                    onError={(e) => (e.target.src = TWA)}
                  />
                  <div className="text-center">
                    <h3 className="font-semibold text-lg">{member.name}</h3>
                    <h4 className="text-sm text-gray-500">{member.role}</h4>
                    <div className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">Email:</span>{" "}
                      <a
                        href={`mailto:${member.email}`}
                        className="text-sky-600 hover:text-sky-800"
                      >
                        {member.email}
                      </a>
                      <p className="text-sm text-gray-600 mt-2 text-left font-['corporate-s']">
                        {member.bio
                          ? truncateBio(member.bio)
                          : "Bio coming soon!"}
                      </p>
                      {member.bio && (
                        <button
                          onClick={() => openStaffModal(member)}
                          className="text-[#003DA5] font-bold text-sm transition-colors mt-2 cursor-pointer"
                          aria-label={`Read more about ${member.name}`}
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Staff Modal */}
      {staffModalOpen && selectedStaff && (
        <div
          aria-modal="true"
          role="dialog"
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
        >
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setStaffModalOpen(false)}
            role="button"
            aria-label="Close modal"
          />
          <div className="relative bg-white max-w-3xl w-full rounded-xl p-6 shadow-lg z-10">
            <button
              onClick={() => setStaffModalOpen(false)}
              type="button"
              className="absolute text-3xl cursor-pointer top-3 right-3 text-gray-500 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-emerald-600 font-bold leading-none"
              aria-label="Close"
            >
              ✕
            </button>
            <div className="flex flex-col md:flex-row gap-6 p-2">
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">
                  {selectedStaff.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {selectedStaff.title || selectedStaff.role}
                </p>
                <p className="text-sm text-gray-600 font-['corporate-s']">
                  {selectedStaff.bio || "Bio coming soon!"}
                </p>
              </div>
              <div className="flex-shrink-0">
                <img
                  src={selectedStaff.image}
                  alt={`${selectedStaff.name}`}
                  className="w-60 h-100 rounded-md object-cover lazy-load p-2 rounded-lg"
                  loading="lazy"
                  onError={(e) => (e.target.src = "/assets/placeholder.jpg")}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {contactOpen && (
        <div
          aria-modal="true"
          role="dialog"
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
        >
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setContactOpen(false)}
            role="button"
            aria-label="Close modal"
          />
          <div className="relative bg-white max-w-lg w-full rounded-xl p-6 shadow-lg z-10">
            <button
              onClick={() => setContactOpen(false)}
              type="button"
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-emerald-600 text-3xl font-bold leading-none"
              aria-label="Close"
            >
              ✕
            </button>
            <ContactForm
              contact={contact}
              setContact={setContact}
              contactStatus={contactStatus}
              handleContactSubmit={handleContactSubmit}
            />
          </div>
        </div>
      )}

      {/* Contact Form Section */}
      <section className="py-16 bg-[#fff]">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#003DA5] mb-8 text-center uppercase font-['corporate-s']">
            Get in Touch
          </h2>
          <p className="text-center text-gray-600 mb-8 font-['corporate-s']">
            Have questions or want to learn more about our programs? Reach out
            to us using the form below.
          </p>
          <ContactForm
            contact={contact}
            setContact={setContact}
            contactStatus={contactStatus}
            handleContactSubmit={handleContactSubmit}
          />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
